import React, { useContext, useState, useReducer, useEffect, useCallback } from 'react';
import { Redirect, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import axios from 'axios';

import Policies from '../common/Policies';
import SelectedPoliciesDispatch from '../../contexts/selectedPolicies.dispatch';
import selectedPolicesReducer from '../../reducers/selectedPolices.reducer';
import AlertDispatch from '../../contexts/alert.dispatch';
import alertActions from '../../actions/alert.actions';
import File from '../common/File';
import Submit from '../common/PrimaryButton';
import CancelRequestValidator from '../../helpers/validators/cancel-request.validator';
import Error from '../common/Error';
import findErrorField from '../../helpers/serverError';
import RadioButton from '../common/RadioButton';
import Textarea from '../common/Textarea';

import style from './CancelationRequestPage.module.css';

const CancelationRequestPage = React.memo(({ clientId }) => {
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState({ description: '', file: '' });
  const [file, setFile] = useState();
  const [policies, setPolicies] = useState([]);
  const [stepTwo, setStepTwo] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const alertDispatch = useContext(AlertDispatch);
  const [selectedCancelationiType, setSelectedCancelationiType] = useState({
    Cancellation: true,
    ExtendedReportingPeriod: false,
    CancellationAndExtendedReportingPeriod: false,
  });

  const radioButtonLabelStyle = {
    borderBottom: '1px solid #d9d8ea',
    flex: '1',
    paddingTop: '16px',
    paddingBottom: '16px',
    marginLeft: '6px',
    fontWeight: '400',
    lineHeight: '1.5',
    color: '#000',
    fontSize: '13px',
  };

  const [selectedPolicesState, selectedPolicesDispatch] = useReducer(
    selectedPolicesReducer,
    {
      ids: [],
    },
  );

  const handleDescriptionChange = event => {
    setDescription(event.target.value);
  };

  const dispatchError = useCallback(() => {
    alertDispatch({
      type: alertActions.ERROR,
      message: 'Something went wrong. Please try again',
    });
    setSubmitted(true);
  }, [alertDispatch]);

  const dispatchSuccess = () => {
    alertDispatch({
      type: alertActions.SUCCESS,
      message:
        'Thank you for your request. A member of your dedicated team will respond within 1 business day.',
    });
    setSubmitted(true);
  };

  useEffect(() => {
    const handleSuccess = ({ data }) => {
      setPolicies(data);
    };

    const handleError = () => {
      dispatchError();
    };

    if (selectedPolicesState.ids.length > 0) {
      setStepTwo(true);

      axios
        .get(
          `${
            process.env.REACT_APP_BASE_URL
          }hub/clients/${clientId}/info?policyIds=${selectedPolicesState.ids.join(
            '&policyIds=',
          )}`,
        )
        .then(handleSuccess)
        .catch(handleError);
    }
  }, [dispatchError, clientId, selectedPolicesState.ids]);

  const handleSelectFile = nweFile => {
    setFile(nweFile);
  };

  const handleSubmit = event => {
    event.preventDefault();

    const handleSuccess = () => {
      dispatchSuccess();
    };

    const handleError = ({ response }) => {
      if (response && response.data && response.data.ValidationErrors) {
        const descriptionError = findErrorField(
          response.data.ValidationErrors,
          'Description',
        );

        const fileError = findErrorField(
          response.data.ValidationErrors,
          'File',
        );

        setErrors({
          description: descriptionError ? descriptionError.Messages[0] : '',
          file: fileError ? fileError.Messages[0] : '',
        });
      } else {
        dispatchError();
      }
    };

    const validation = CancelRequestValidator.validate({
      description,
      file,
    });

    setErrors({
      description: validation.description.message,
      file: validation.file.message,
    });

    const getServiceRequestType = () => {
      if (selectedCancelationiType.Cancellation) {
        return 'Cancellation';
      }

      if (selectedCancelationiType.ExtendedReportingPeriod) {
        return 'ExtendedReportingPeriod';
      }

      return 'CancellationAndExtendedReportingPeriod';
    };

    if (validation.isValid) {
      axios
        .post(
          `${
            process.env.REACT_APP_BASE_URL
          }hub/clients/${clientId}/requests/other?policyIds=${selectedPolicesState.ids.join(
            '&policyIds=',
          )}`,
          {
            File: file
              ? {
                  Name: file.FileName,
                  Content: file.Content,
                  ContentType: file.ContentType,
                }
              : null,
            Description: description,
            ServiceRequestType: getServiceRequestType(),
          },
          {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          },
        )
        .then(handleSuccess)
        .catch(handleError);
    }
  };

  const handleCancelationTypeSelect = event => {
    switch (event.target.value) {
      case 'cancelation':
        setSelectedCancelationiType({
          Cancellation: true,
          ExtendedReportingPeriod: false,
          CancellationAndExtendedReportingPeriod: false,
        });
        break;
      case 'erp':
        setSelectedCancelationiType({
          Cancellation: false,
          ExtendedReportingPeriod: true,
          CancellationAndExtendedReportingPeriod: false,
        });
        break;
      default:
        setSelectedCancelationiType({
          Cancellation: false,
          ExtendedReportingPeriod: false,
          CancellationAndExtendedReportingPeriod: true,
        });
        break;
    }
  };

  const showPolicies = () => (
    <SelectedPoliciesDispatch.Provider value={selectedPolicesDispatch}>
      <div className={style.main}>
        <h1 className={style.title}>Request Cancel/ERP</h1>
        <span className={style.hint}>
          Select the policy you wish to cancel OR request Extended Reporting
          Period (ERP)
        </span>
        <Policies
          submitBtnText="Continue"
          clientId={clientId}
          requestType="RequestPolicyDocument"
        />
      </div>
    </SelectedPoliciesDispatch.Provider>
  );

  const showForm = () => (
    <div className={style.main}>
      <h1 className={style.title}>Request Cancel/ERP</h1>
      <div className={style.submitForm}>
        <div className={`form-group ${style.info}`}>
          <div className={style.label}>Business Name: </div>
          {policies.length > 0 && policies[0].BusinessName}
        </div>
        <div className={`form-group ${style.info}`}>
          <div className={style.label}>Line of Business: </div>
          {policies.length > 0 &&
            policies.map(item => item.LineOfBusiness).join(', ')}
        </div>
        <div className={`form-group ${style.info}`}>
          <div className={style.label}>Carrier: </div>
          {policies.length > 0 && policies.map(item => item.Carrier).join(', ')}
        </div>
        <div className={`form-group ${style.info}`}>
          <div className={style.label}>Policy Number: </div>
          {policies.length > 0 &&
            policies.map(item => item.PolicyNumber).join(', ')}
        </div>
        <div className={`form-group ${style.info}`}>
          <div className={style.label}>Please select an option below:</div>
          <div className={style.cancelationTypes}>
            <RadioButton
              name="cancelationType"
              value="cancelation"
              label="Cancellation"
              checked={selectedCancelationiType.Cancellation}
              labelStyles={radioButtonLabelStyle}
              onChange={handleCancelationTypeSelect}
            />
            <RadioButton
              name="cancelationType"
              value="erp"
              label="Extended Reporting Period (ERP)"
              labelStyles={radioButtonLabelStyle}
              checked={selectedCancelationiType.ExtendedReportingPeriod}
              onChange={handleCancelationTypeSelect}
            />
            <RadioButton
              name="cancelationType"
              value="cancellationAndErp"
              label="Cancellation AND Extended Reporting Period"
              labelStyles={radioButtonLabelStyle}
              checked={
                selectedCancelationiType.CancellationAndExtendedReportingPeriod
              }
              onChange={handleCancelationTypeSelect}
            />
          </div>
        </div>
        <div className={`form-group ${style.info}`}>
          <div className={style.label}>Description of your Request:</div>
          <Textarea
            name="description"
            value={description}
            onChange={handleDescriptionChange}
          />
          <Error error={errors.description} />
        </div>
        <div className={`form-group ${style.info}`}>
          <div className={style.label}>Attach a cancellation/ERP request:</div>
          <File clientId={clientId} handleSelectFile={handleSelectFile} />
          <div>
            <Error error={errors.file} />
          </div>
        </div>
        <div className={style.actions}>
          <Submit
            type="submit"
            text="Submit Request"
            styles={{ width: '200px' }}
            onClick={handleSubmit}
          />
          <Link to={`/clients/${clientId}`} className={style.cancelBtn}>
            Cancel
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {!stepTwo && showPolicies()}
      {stepTwo && showForm()}
      {submitted && <Redirect to={`/clients/${clientId}`} />}
    </>
  );
});

CancelationRequestPage.propTypes = {
  clientId: PropTypes.string.isRequired,
};

export default CancelationRequestPage;
